<template>
    <div>
      <Filter />
      <div v-if="loading" class="loading-shimmer w-full h-48 mt-4 rounded-lg"></div>
      <div v-else class="container mx-auto p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        <PropertyCard
          v-for="(property, index) in properties"
          :key="index"
          :image="property.image"
          :price="property.price"
          :description="property.name"
          :bedrooms="property.bedrooms"
          :bathrooms="property.bathrooms"
          :carport="property.carport"
          :landSize="property.surface"
          :buildingSize="property.buildingSize"
          :agentImage="defaultAgentImage"
          :agentName="defaultAgentName"
          :whatsappLink="defaultWhatsappLink"
        />
      </div>
      <div class="pagination mt-4 flex justify-center">
        <button
          @click="goToPage(currentPage - 1)"
          :disabled="currentPage === 1"
          class="px-4 py-2 bg-primary text-white rounded"
        >
          Previous
        </button>
        <span class="mx-2">
          Page {{ currentPage }} of {{ totalPages }}
        </span>
        <button
          @click="goToPage(currentPage + 1)"
          :disabled="currentPage === totalPages"
          class="px-4 py-2 bg-primary text-white rounded"
        >
          Next
        </button>
      </div>
    </div>
  </template>

  <script lang="ts">
  import { defineComponent, ref, onMounted } from 'vue';
  import PropertyCard from '@/components/home/PropertyCard.vue';
  import Filter from '@/components/home/Filter.vue';
  import { useAuthStore } from '@/stores/authStore';
  import { getPropertyList } from '@/utils/apiService';

  export default defineComponent({
    components: {
      PropertyCard,
      Filter,
    },
    setup() {
      const authStore = useAuthStore();
      const properties = ref([]);
      const currentPage = ref(1);
      const pageSize = ref(9); // Number of properties per page
      const totalPages = ref(1);
      const loading = ref(false); // Loading state
      const defaultAgentImage = 'default_agent_image_url';
      const defaultAgentName = 'Agent Name';
      const defaultWhatsappLink = 'whatsapp-link-placeholder';

      const fetchListingList = async () => {
        loading.value = true; // Show loading indicator
        try {
          const param = {
            company_id: 0,
            country_id: 0,
            city: '',
            name: '',
            type: '',
            state_id: 0,
            limit: pageSize.value,
            offset: (currentPage.value - 1) * pageSize.value,
            owner_id: 0,
          };
          const response = await getPropertyList(param);

          // Update properties and pagination info based on response data
          properties.value = response.data.map((property: any) => ({
            image: property.property_image_ids.length ? `${property.property_image_ids[0].url}` : 'default_image_url',
            price:  `Rp. ${property.price ? formatCurrencyIDR(is_trial_mode) : 0}`,
            name: property.name || 'No description available',
            bedrooms: property.bedroom,
            bathrooms: property.bathroom,
            carport: property.carport_capacity,
            surface: property.surface,
            buildingSize: property.building_size,
          }));

          // Calculate total pages based on total data and page size
          totalPages.value = Math.ceil(response.total_data / pageSize.value);
        } catch (error) {
          console.error('Failed to fetch properties', error);
        } finally {
          loading.value = false; // Hide loading indicator
        }
      };

      const goToPage = (page: number) => {
        if (page > 0 && page <= totalPages.value) {
          currentPage.value = page;
          fetchListingList();
        }
      };

      onMounted(() => {
        fetchListingList();
      });

      return {
        properties,
        currentPage,
        totalPages,
        goToPage,
        loading,
        defaultAgentImage,
        defaultAgentName,
        defaultWhatsappLink,
      };
    },
  });
  </script>

  <style scoped>
  .loading-shimmer {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
  }

  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
  </style>
